import { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Button,
  MenuItem,
  Select,
  TextField,
  Theme,
  Typography,
  useTheme,
} from '@mui/material'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import {
  capitalize,
  PAIN_HISTORY_NAMES,
  PAIN_HISTORY_TEXT_FIELDS,
  PAIN_HISTORY_TOGGLE_FIELDS,
  toastMessages,
} from '../constants'
import useCustomStyles from '../../../../utils/hooks/CustomStylesHook'
import { useAppSelector } from '../../../../app/hooks'
import {
  isInitialLoadForOPDForms,
  isPainHistoryOpdFormSubmitted,
  selectPainHistoryPhysio,
  setIsPainHistoryOpdFormSubmit,
  setLoading,
  updatePainHistoryPhysio,
} from '../opdFormsSlice'
import { useParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '../../../../app/store'
import { toast } from 'react-toastify'
import {
  opdFormDataFetchApi,
  opdFormDataSaveApi,
} from '../../../../services/OpdFormsSTTService'
import {
  GET_OPD_PAINHISTORY_ENDPOINT,
  POST_OPD_PAINHISTORY_ENDPOINT,
} from '../../../../utils/Endpoints'

const styles = (theme: Theme) => ({
  painHistoryDropdown: {
    width: '306px',
    height: '25px',
    backgroundColor: '#fff',
    borderRadius: 4,
    '& .MuiSelect-root': {
      fontSize: '12px',
    },
  },
  textFieldStyle: {
    '& .MuiInputBase-input': {
      fontSize: '12px',
      minWidth: '257px',
      padding: '4px',
    },
  },
  fieldLabel: {
    textAlign: 'right',
    textWrap: 'wrap',
    fontSize: '12px',
    [theme.breakpoints.between('sm', 'md')]: {
      minWidth: '173px',
    },
    [theme.breakpoints.up('md')]: {
      minWidth: '179px',
    },
  },
  textFieldWhiteBg: {
    backgroundColor: '#ffffff',
  },
  customFontBox: {
    '& .MuiTypography-root': {
      fontFamily: 'Noto Sans, sans-serif',
      color: '#363463',
    },
    '& .MuiButton-root': {
      fontFamily: 'Open Sans, sans-serif',
      fontSize: '12px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      textTransform: 'capitalize',
      width: '147px',
      margin: '2px',
      justifyContent: 'center',
      color: '#6c757d',
    },
    '& .MuiButton-contained': {
      backgroundColor: '#4682b4 !important',
      color: '#f1f3f5 !important',
      fontWeight: 600,
      border: '1px solid #4682b4 !important',
    },
  },
  submitButtonContainer: {
    [theme.breakpoints.up('md')]: {
      width: '82%',
    },
    [theme.breakpoints.between('sm', 'md')]: {
      width: '75%',
    },
  },
})

const PainHistoryPhysio = () => {
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)
  const { id } = useParams()
  const painHistoryPhysio: any = useAppSelector(selectPainHistoryPhysio)
  const dispatch = useDispatch<AppDispatch>()
  const visitType = 'physio'
  const [fhirRecordId, setFhirRecordId] = useState<string | null>(null)
  const isInitialLoadForForm = useAppSelector(isInitialLoadForOPDForms)
  const isFormSubmitted = useAppSelector(isPainHistoryOpdFormSubmitted)

  const initialValues = {
    intensivity: painHistoryPhysio?.intensivity || '',
    selectedNumber: painHistoryPhysio?.intensivity || '',
    painConstant: painHistoryPhysio?.painConstant || '',
    sensoryQualities: painHistoryPhysio?.sensoryQualities || '',
    onset: painHistoryPhysio?.onset || '',
    whatRelievesPain: painHistoryPhysio?.whatRelievesPain || '',
    accompanyingSymptoms: painHistoryPhysio?.accompanyingSymptoms || '',
    physicalActivity: painHistoryPhysio?.physicalActivity || '',
    concentration: painHistoryPhysio?.concentration || '',
  }

  const validationSchema = Yup?.object({
    onset: Yup?.string(),
    whatRelievesPain: Yup?.string(),
    accompanyingSymptoms: Yup?.string(),
  })

  const formik: any = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      handlePainHistoryPhysioSubmitForm(values, setSubmitting)
    },
    enableReinitialize: true,
  })

  const handlePainHistoryPhysioSubmitForm = async (
    data: Record<string, any>,
    setSubmitting: any
  ) => {
    setSubmitting(true)

    // Value of intensivity field is stored in selectedNumber field
    data.intensivity = data.selectedNumber

    const payload = {
      encounterId: id,
      visitType: `opd/${visitType}`,
      fhirRecordId: fhirRecordId,
      ...data,
    }

    const response = await opdFormDataSaveApi(
      POST_OPD_PAINHISTORY_ENDPOINT,
      payload
    )

    if (response?.success) {
      dispatch(setIsPainHistoryOpdFormSubmit(true))
      toast.success(response?.message, { autoClose: 800 })
    } else {
      toast.error(response?.message || toastMessages.Form_Submission_Error, {
        autoClose: 800,
      })
    }
    setSubmitting(false)
  }

  const getPainHistory = useCallback(async () => {
    if (!isInitialLoadForForm && !isFormSubmitted) return
    const getPainHistoryUrl = `${GET_OPD_PAINHISTORY_ENDPOINT}${id}&visitType=opd/${visitType}`

    const response = await opdFormDataFetchApi(getPainHistoryUrl)

    if (response?.success) {
      const result = response?.data
      const fhirRecordId = response?.data?.fhirRecordId
      setFhirRecordId(fhirRecordId)

      dispatch(
        updatePainHistoryPhysio({
          accompanyingSymptoms: result?.accompanyingSymptoms,
          intensivity: result?.intensivity,
          painConstant: capitalize(result?.painConstant),
          sensoryQualities: capitalize(result?.sensoryQualities),
          onset: result?.onset,
          whatRelievesPain: result?.relievesPain,
          physicalActivity: capitalize(result?.physicalActivity),
          concentration: capitalize(result?.concentration),
        })
      )
    } else {
      toast.error(toastMessages.Fetch_Failed)
    }
    dispatch(setLoading(false))
  }, [dispatch, id, visitType, isInitialLoadForForm, isFormSubmitted])

  useEffect(() => {
    getPainHistory()
  }, [getPainHistory])

  return (
    <Box
      display={'flex'}
      flexDirection={'column'}
      justifyContent={'center'}
      gap={1}
      width={'fit-content'}
      className={classes?.customFontBox}
    >
      <Box
        display={'flex'}
        justifyContent={'start'}
        alignItems={'center'}
        width={'100%'}
        flexWrap={'wrap'}
        gap={1}
      >
        <Typography className={classes?.fieldLabel}>
          Intensity&nbsp;:
        </Typography>
        <Select
          className={classes?.painHistoryDropdown}
          name={PAIN_HISTORY_NAMES.SelectedNumber}
          value={formik?.values.selectedNumber || ''}
          onChange={formik?.handleChange}
          onBlur={formik?.handleBlur}
          displayEmpty
        >
          <MenuItem value='' disabled>
            Select an option
          </MenuItem>
          {Array?.from({ length: 11 }, (_, i) => (
            <MenuItem key={i} value={i}>
              {i}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {PAIN_HISTORY_TOGGLE_FIELDS?.slice(0, 2)?.map(
        ({ name, label, options }) => (
          <Box
            display='flex'
            justifyContent='start'
            alignItems='center'
            width='100%'
            key={name}
            flexWrap='wrap'
            gap={1}
          >
            <Typography className={classes?.fieldLabel}>{label}:</Typography>

            {name === PAIN_HISTORY_NAMES.SensoryQualities ? (
              <Box display='flex' flexDirection='column' gap={'4px'}>
                {[0, 1, 2]?.map((rowIndex) => (
                  <Box key={rowIndex} display='flex' gap={1}>
                    {options
                      ?.slice(rowIndex * 2, rowIndex * 2 + 2)
                      ?.map((option) => {
                        const isSelected =
                          formik?.values[name as keyof typeof formik.values] ===
                          option

                        return (
                          <Button
                            key={option}
                            variant={isSelected ? 'contained' : 'outlined'}
                            color={isSelected ? 'primary' : 'inherit'}
                            onClick={() => formik?.setFieldValue(name, option)}
                          >
                            {option}
                          </Button>
                        )
                      })}
                  </Box>
                ))}
              </Box>
            ) : (
              <Box display='flex' flexWrap='wrap' gap={'4px'}>
                {options?.map((option) => {
                  const isSelected =
                    formik?.values[name as keyof typeof formik.values] ===
                    option

                  return (
                    <Button
                      key={option}
                      variant={isSelected ? 'contained' : 'outlined'}
                      color={isSelected ? 'primary' : 'inherit'}
                      onClick={() => formik?.setFieldValue(name, option)}
                    >
                      {option}
                    </Button>
                  )
                })}
              </Box>
            )}
          </Box>
        )
      )}

      {PAIN_HISTORY_TEXT_FIELDS?.map(({ name, label }) => (
        <Box
          display={'flex'}
          justifyContent={'start'}
          alignItems={'center'}
          width={'100%'}
          key={name}
          flexWrap={'wrap'}
          gap={2}
        >
          <Typography className={classes?.fieldLabel}>{label}:</Typography>

          <TextField
            multiline
            rows={3}
            variant={'outlined'}
            name={name}
            inputProps={{ 'data-testid': name }}
            value={formik?.values[name as keyof typeof formik.values] || ''}
            onChange={formik?.handleChange}
            onBlur={formik?.handleBlur}
            error={
              formik?.touched[name as keyof typeof formik.values] &&
              Boolean(formik?.errors[name as keyof typeof formik.errors])
            }
            className={`${classes?.textFieldStyle} ${classes?.textFieldWhiteBg}`}
          />
        </Box>
      ))}

      {PAIN_HISTORY_TOGGLE_FIELDS?.slice(2, 5)?.map(
        ({ name, label, options }) => (
          <Box
            display='flex'
            justifyContent='start'
            alignItems='center'
            width='100%'
            key={name}
            flexWrap='wrap'
            gap={'4px'}
          >
            <Typography className={classes?.fieldLabel}>{label}:</Typography>

            <Box display='flex' flexWrap='wrap' gap={1}>
              {options?.map((option) => {
                const isSelected =
                  formik?.values[name as keyof typeof formik.values] === option

                return (
                  <Button
                    key={option}
                    variant={isSelected ? 'contained' : 'outlined'}
                    color={isSelected ? 'primary' : 'inherit'}
                    onClick={() => formik?.setFieldValue(name, option)}
                  >
                    {option}
                  </Button>
                )
              })}
            </Box>
          </Box>
        )
      )}

      <Box
        display={'flex'}
        alignItems={'center'}
        justifyContent={'flex-end'}
        className={classes?.submitButtonContainer}
      >
        <Button
          variant='contained'
          color='primary'
          onClick={formik?.submitForm}
          className={classes?.submitBtn}
        >
          Submit
        </Button>
      </Box>
    </Box>
  )
}

export default PainHistoryPhysio
